"use client";

import axios from "axios";

import { useAuthStore } from "@/stores/auth-store";

// The access token now lives in the in-memory auth store (#39). Fall back to a
// transient localStorage token only for the mobile WebView (which injects it) and the
// MFA-challenge partial token (written before the store session is established).
function getAccessToken(): string | null {
  const fromStore = useAuthStore.getState().user?.token;
  if (fromStore) return fromStore;
  if (typeof window !== "undefined") return localStorage.getItem("auth-token");
  return null;
}

// Client-side API that goes through proxy to backend
export const apiClient = axios.create({
  baseURL: "/api", // Proxy will handle routing
  headers: {
    "Content-Type": "application/json",
  },
});

// Only add interceptors on client-side (interceptors run at module load time)
if (typeof window !== "undefined") {
  // Add request interceptor to include auth token
  apiClient.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Add response interceptor to handle token refresh on 401 errors.
  //
  // This delegates to the auth store's coordinated `refreshToken()` rather than
  // doing its own raw fetch (audit Track A, A1/A3). That single shared routine:
  //   - coalesces all concurrent 401s behind one in-flight promise (no storm of
  //     parallel /refresh calls each rotating the single-use token),
  //   - holds a cross-tab lock so only one tab rotates,
  //   - logs out ONLY on a confirmed invalid-token 401 (transient 5xx/network
  //     failures retry and leave the session intact),
  //   - tears down consistently via `store.logout()` (route guards then redirect
  //     to /auth/login) instead of a raw localStorage wipe + hard navigation.
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If we get a 401 and haven't already tried to refresh this request
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          const refreshed = await useAuthStore.getState().refreshToken();

          if (refreshed) {
            // Replay the original request with the freshly rotated (in-memory) token.
            const newToken = getAccessToken();
            if (newToken) {
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return apiClient(originalRequest);
          }

          // Not refreshed. `refreshToken()` has already either logged out (on a
          // confirmed invalid-token 401 — route guards handle the redirect) or
          // left the session intact (on a transient failure). Either way, just
          // surface the original error to the caller.
        } catch (refreshError) {
          console.warn("Token refresh failed in interceptor:", refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}
